import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Printer, FileText, User, AlertTriangle, ClipboardList } from 'lucide-react';
import ShelterCareConnectLogo from './ui/logo';

const ResidentDocumentPrint = ({ 
  isOpen, 
  onClose, 
  resident, 
  caseNotes = [], 
  incidentReports = [], 
  consents = [],
  printType = 'resident_profile' 
}) => {
  const printRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    return new Date(dateTimeString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const renderResidentProfile = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center border-b-2 border-gray-300 pb-4">
        <div className="flex items-center justify-center space-x-4 mb-2">
          <ShelterCareConnectLogo size="lg" showText={false} />
          <h1 className="text-2xl font-bold text-gray-900">Resident Profile Report</h1>
        </div>
        <p className="text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
      </div>

      {/* Resident Information */}
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-3">
          <h2 className="text-lg font-semibold border-b border-gray-200 pb-1">Personal Information</h2>
          <div className="space-y-2 text-sm">
            <div><strong>Name:</strong> {resident?.first_name} {resident?.last_name}</div>
            <div><strong>Date of Birth:</strong> {formatDate(resident?.date_of_birth)}</div>
            <div><strong>SSN:</strong> {resident?.ssn || 'Not provided'}</div>
            <div><strong>Gender:</strong> {resident?.gender || 'Not specified'}</div>
            <div><strong>Phone:</strong> {resident?.phone || 'Not provided'}</div>
            <div><strong>Email:</strong> {resident?.email || 'Not provided'}</div>
          </div>
        </div>

        <div className="space-y-3">
          <h2 className="text-lg font-semibold border-b border-gray-200 pb-1">Shelter Information</h2>
          <div className="space-y-2 text-sm">
            <div><strong>Check-in Date:</strong> {formatDate(resident?.check_in_date)}</div>
            <div><strong>Status:</strong> {resident?.status || 'Active'}</div>
            <div><strong>Room Assignment:</strong> {resident?.room_assignment || 'Unassigned'}</div>
            <div><strong>Emergency Contact:</strong> {resident?.emergency_contact || 'Not provided'}</div>
            <div><strong>Emergency Phone:</strong> {resident?.emergency_phone || 'Not provided'}</div>
            <div><strong>Medical Needs:</strong> {resident?.medical_needs || 'None reported'}</div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      {resident?.notes && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold border-b border-gray-200 pb-1">Additional Notes</h2>
          <p className="text-sm bg-gray-50 p-3 rounded">{resident.notes}</p>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{caseNotes.length}</div>
          <div className="text-sm text-gray-600">Total Case Notes</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">{incidentReports.length}</div>
          <div className="text-sm text-gray-600">Incident Reports</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {resident?.check_in_date ? Math.floor((new Date() - new Date(resident.check_in_date)) / (1000 * 60 * 60 * 24)) : 0}
          </div>
          <div className="text-sm text-gray-600">Days in Shelter</div>
        </div>
      </div>
    </div>
  );

  const renderCaseNotes = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center border-b-2 border-gray-300 pb-4">
        <div className="flex items-center justify-center space-x-4 mb-2">
          <FileText className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Case Notes Report</h1>
        </div>
        <p className="text-gray-600">
          For: {resident?.first_name} {resident?.last_name} | Generated on {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* Case Notes List */}
      <div className="space-y-4">
        {caseNotes.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No case notes found for this resident.</p>
        ) : (
          caseNotes.map((note, index) => (
            <div key={index} className="border border-gray-300 rounded-lg p-4 page-break-inside-avoid">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-lg">Case Note #{index + 1}</h3>
                  <p className="text-sm text-gray-600">
                    By: {note.staff_name || note.created_by || 'Unknown Staff'} | Date: {formatDateTime(note.timestamp || note.created_at)}
                  </p>
                </div>
                {note.is_urgent && (
                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium">
                    URGENT
                  </span>
                )}
                {note.type && (
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                    {note.type}
                  </span>
                )}
              </div>
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{note.note || note.content}</p>
              </div>
              {note.follow_up_date && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm text-orange-600">
                    <strong>Follow-up Required:</strong> {formatDate(note.follow_up_date)}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderIncidentReports = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center border-b-2 border-gray-300 pb-4">
        <div className="flex items-center justify-center space-x-4 mb-2">
          <AlertTriangle className="h-8 w-8 text-red-600" />
          <h1 className="text-2xl font-bold text-gray-900">Incident Reports</h1>
        </div>
        <p className="text-gray-600">
          For: {resident?.first_name} {resident?.last_name} | Generated on {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* Incident Reports List */}
      <div className="space-y-4">
        {incidentReports.length === 0 ? (
          <p className="text-center text-gray-500 py-8">No incident reports found for this resident.</p>
        ) : (
          incidentReports.map((incident, index) => (
            <div key={index} className="border border-gray-300 rounded-lg p-4 page-break-inside-avoid">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <h3 className="font-semibold text-lg">{incident.incident_type}</h3>
                  <p className="text-sm text-gray-600">
                    Date: {formatDateTime(incident.incident_date)}
                  </p>
                  <p className="text-sm text-gray-600">
                    Reported by: {incident.reported_by}
                  </p>
                </div>
                <div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      incident.severity === 'high' ? 'bg-red-100 text-red-800' :
                      incident.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {incident.severity ? incident.severity.toUpperCase() : 'LOW'} SEVERITY
                    </span>
                  </div>
                  {incident.location && (
                    <p className="text-sm text-gray-600 mt-2">Location: {incident.location}</p>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-1">Description:</h4>
                  <p className="text-sm whitespace-pre-wrap bg-gray-50 p-2 rounded">{incident.description}</p>
                </div>

                {incident.action_taken && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-700 mb-1">Action Taken:</h4>
                    <p className="text-sm whitespace-pre-wrap bg-blue-50 p-2 rounded">{incident.action_taken}</p>
                  </div>
                )}

                {incident.follow_up_required && (
                  <div>
                    <h4 className="font-medium text-sm text-gray-700 mb-1">Follow-up Notes:</h4>
                    <p className="text-sm whitespace-pre-wrap bg-orange-50 p-2 rounded">{incident.follow_up_notes}</p>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderCombinedReport = () => (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center border-b-2 border-gray-300 pb-4">
        <div className="flex items-center justify-center space-x-4 mb-2">
          <User className="h-8 w-8 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">Complete Resident Documentation</h1>
        </div>
        <p className="text-gray-600">
          For: {resident?.first_name} {resident?.last_name} | Generated on {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* Resident Profile Section */}
      <div className="page-break-after">
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-300 pb-2">Resident Profile</h2>
        {renderResidentProfile()}
      </div>

      {/* Case Notes Section */}
      <div className="page-break-after">
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-300 pb-2">Case Notes History</h2>
        {renderCaseNotes()}
      </div>

      {/* Incident Reports Section */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-300 pb-2">Incident Reports</h2>
        {renderIncidentReports()}
      </div>

      {/* Signed Consents Section */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-300 pb-2">Signed Consents &amp; Agreements</h2>
        {renderConsents()}
      </div>
    </div>
  );

  const renderConsents = () => (
    <div className="space-y-4">
      {(!consents || consents.length === 0) ? (
        <p className="text-center text-gray-500 py-6">No signed consents on file.</p>
      ) : (
        consents.map((c, i) => (
          <div key={i} className="border border-gray-300 rounded-lg p-4 page-break-inside-avoid">
            <div className="flex justify-between items-start">
              <h3 className="font-semibold text-lg">{c.document_title}</h3>
              <span className="text-sm text-gray-600">{c.resident_signed_at ? new Date(c.resident_signed_at).toLocaleString() : ''}</span>
            </div>
            {c.document_body && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{c.document_body}</p>}
            {c.items_received && c.items_received.length > 0 && (
              <p className="text-sm mt-2"><span className="font-medium">Items received:</span> {c.items_received.join(', ')}</p>
            )}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div><div className="border-b border-gray-400 pb-1 text-sm">{c.resident_signature}</div><div className="text-xs text-gray-500">Resident signature</div></div>
              {c.staff_signature && <div><div className="border-b border-gray-400 pb-1 text-sm">{c.staff_signature}</div><div className="text-xs text-gray-500">Staff signature</div></div>}
              {c.witness_signature && <div><div className="border-b border-gray-400 pb-1 text-sm">{c.witness_signature}</div><div className="text-xs text-gray-500">Witness</div></div>}
            </div>
          </div>
        ))
      )}
    </div>
  );

  const getReportContent = () => {
    switch (printType) {
      case 'resident_profile':
        return (
          <div className="space-y-8">
            {renderResidentProfile()}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-300 pb-2">Signed Consents &amp; Agreements</h2>
              {renderConsents()}
            </div>
          </div>
        );
      case 'case_notes':
        return renderCaseNotes();
      case 'incident_reports':
        return renderIncidentReports();
      case 'combined_report':
        return renderCombinedReport();
      default:
        return renderResidentProfile();
    }
  };

  const getReportTitle = () => {
    switch (printType) {
      case 'resident_profile':
        return 'Resident Profile';
      case 'case_notes':
        return 'Case Notes Report';
      case 'incident_reports':
        return 'Incident Reports';
      case 'combined_report':
        return 'Complete Documentation';
      default:
        return 'Resident Report';
    }
  };

  return (
    <>
      {/* Dialog for preview */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center space-x-2">
                <Printer className="h-5 w-5" />
                <span>Print {getReportTitle()}</span>
              </DialogTitle>
              <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
                <Printer className="h-4 w-4 mr-2" />
                Print Report
              </Button>
            </div>
          </DialogHeader>

          <div className="mt-4">
            <div ref={printRef} className="print-content bg-white p-8">
              {getReportContent()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print styles */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          
          /* Page break controls */
          .page-break-after {
            page-break-after: always;
          }
          .page-break-inside-avoid {
            page-break-inside: avoid;
          }
          
          /* Hide dialog and other UI elements when printing */
          .fixed, .absolute {
            display: none !important;
          }
          
          /* Ensure print content is visible */
          .print-content {
            display: block !important;
            position: static !important;
          }
          
          /* Typography adjustments for print */
          .print-content h1 { font-size: 24px; }
          .print-content h2 { font-size: 20px; }
          .print-content h3 { font-size: 18px; }
          .print-content h4 { font-size: 16px; }
          .print-content p, .print-content div { font-size: 14px; }
          
          /* Spacing adjustments */
          .print-content {
            padding: 0.5in;
            line-height: 1.4;
          }
          
          /* Border and background adjustments */
          .print-content .border { border-width: 1px !important; }
          .print-content .bg-gray-50 { background-color: #f9f9f9 !important; }
          .print-content .bg-blue-50 { background-color: #eff6ff !important; }
          .print-content .bg-orange-50 { background-color: #fff7ed !important; }
        }
      `}</style>
    </>
  );
};

export default ResidentDocumentPrint;